import api, { ApiResult } from '../../../api/api-caller';
import { ApiEndpoint } from '../../../api/api-consts';
import { fetchRangeColors, getRangeColors, RangeColor } from '../../../utils/rangecolor-utils';
import initialState, {
  BusinessUnit,
  CorporateBusinessCanvasState,
  Titles
} from '../corporate-business-canvas-state';

async function doRequestRangeColors(businessUnits: BusinessUnit[]): Promise<void> {
  const measureIds: string[] = businessUnits
    .map((businessUnit: any) => businessUnit.measureId)
    .filter((measureId: string | undefined) => measureId !== undefined && measureId !== '');

  if (measureIds.length === 0) return;

  const rangeColorsResult = await fetchRangeColors(measureIds);

  if (!rangeColorsResult.ok) throw new Error(rangeColorsResult.data.errorMessage);

  const rangeColors: RangeColor[] = getRangeColors(rangeColorsResult);

  rangeColors.forEach(rangeColor => {
    const foundList = businessUnits.filter(
      businessUnit => businessUnit.measureId === rangeColor.id
    );

    if (foundList.length > 0) {
      for (let i = 0; i < foundList.length; i += 1) {
        foundList[i].rangecolor =
          rangeColor.rangecolor === '000000' ? undefined : `#${rangeColor.rangecolor}`;
      }
    }
  });
}

const doRequestBusinessUnits = async (parentId: string): Promise<ApiResult> => {
  const call = {
    body: {
      parentId,
      objecttype: 'Business Model Diagram',
      attributes:
        'name(as="title"),description(as="subtitle"), id, fullid, functionasattribute(functionasattribute="scorecardlinks", as="measureId")'
    },
    method: 'POST'
  };

  const result = await api.asyncCallApi(ApiEndpoint.childelements, call);

  return result;
};

const doGetBusinessUnits = (businessUnitsResult: ApiResult): BusinessUnit[] => {
  const businessUnitsApiData: BusinessUnit[] = businessUnitsResult.data.map(
    (aObject: BusinessUnit) => {
      return { ...aObject, titleUrl: `${aObject.id}/` };
    }
  );

  return businessUnitsApiData;
};

const doRequestTitles = async (parentId: string): Promise<ApiResult> => {
  const call = {
    body: {
      query: `[${parentId}]`,
      attributes: 'name(as="title"),description(as="subtitle")'
    },
    method: 'POST'
  };

  const result: ApiResult = await api.asyncCallApi(ApiEndpoint.meaquery, call);
  return result;
};

const doGetTitles = (titlesResult: ApiResult): Titles => {
  return titlesResult.data.pop();
};

const doGetData = async (parentId: string): Promise<CorporateBusinessCanvasState> => {
  try {
    const [titlesResult, businessUnitsResult] = await Promise.all([
      doRequestTitles(parentId),
      doRequestBusinessUnits(parentId)
    ]);

    if (!titlesResult.ok || !businessUnitsResult.ok) {
      return {
        ...initialState,
        error: titlesResult.error ||
          businessUnitsResult.error || { message: 'Something went wrong', details: '' }
      };
    }

    if (!titlesResult.data[0]?.title)
      return {
        ...initialState,
        error: {
          message: 'No data.',
          details: `Element with id ${parentId} was not found. Check the element id given in URL.`
        }
      };

    const titles = doGetTitles(titlesResult);
    const businessUnits = doGetBusinessUnits(businessUnitsResult);

    try {
      await doRequestRangeColors(businessUnits);
    } catch (error) {
      return {
        ...initialState,
        businessUnits,
        titles,
        error: { message: 'Could not get range colors', details: JSON.stringify(error) }
      };
    }

    return { ...initialState, businessUnits, titles };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface CBCViewModelType {
  fetchData(): Promise<CorporateBusinessCanvasState>;
  getData(): Promise<CorporateBusinessCanvasState>;
  getId(): string;
}

const createCorporateBusinessCanvasViewModel = (rootElementId: string): CBCViewModelType => {
  let data: CorporateBusinessCanvasState;
  const getId = (): string => rootElementId;
  const fetchData = async (): Promise<CorporateBusinessCanvasState> => {
    if (viewModelCache[rootElementId]) {
      console.log('From cache', viewModelCache[rootElementId]);
      data = viewModelCache[rootElementId];
    } else {
      data = await doGetData(rootElementId);
      viewModelCache[rootElementId] = data;
    }
    return data;
  };

  const getData = async (): Promise<CorporateBusinessCanvasState> => {
    if (data) {
      console.log('getData already populated');
      return data;
    }
    await fetchData();
    return data;
  };

  return {
    getId,
    fetchData,
    getData
  };
};

export default createCorporateBusinessCanvasViewModel;
